import React, { createContext, useState, useContext, useEffect } from 'react';
import supabase from '../supabase/supabase';
import { useAuth } from './AuthContext';

const mockTestimonials = [
  {
    id: 'mock-1',
    source: 'Google',
    authorName: "Sarah Jenkins",
    authorRole: "CMO",
    companyName: "TechFlow",
    clientAvatarUrl: "",
    content: "Moment of Truth increased our conversion rate by 42% in the first month. Absolutely essential tool.",
    rating: 5,
    date: "2023-10-15",
    status: 'verified',
    isPublished: true
  },
  {
    id: 'mock-2',
    source: 'Custom',
    authorName: "Marcus Chen",
    authorRole: "Founder",
    companyName: "SaaSify",
    clientAvatarUrl: "",
    content: "The widgets are stunning. They match our premium brand perfectly without any custom CSS.",
    rating: 5,
    date: "2023-10-18",
    status: 'verified',
    isPublished: true
  },
  {
    id: 'mock-3',
    source: 'Custom',
    authorName: "Elena Rodriguez",
    authorRole: "VP Growth",
    companyName: "Nexus",
    clientAvatarUrl: "",
    content: "Finally, a social proof tool that doesn't look like it was built in 2010. Pure elegance.",
    rating: 5,
    date: "2023-10-20",
    status: 'under review',
    isPublished: true
  },
  {
    id: 'mock-4',
    source: 'Youtube',
    authorName: "David Kim",
    authorRole: "Marketing Director",
    companyName: "Elevate",
    clientAvatarUrl: "",
    content: "Integration took 5 minutes. The ROI has been immeasurable. Highly recommend.",
    rating: 5,
    date: "2023-10-21",
    status: 'verified',
    isPublished: true
  },
  {
    id: 'mock-5',
    source: 'Google',
    authorName: "Alex Thompson",
    authorRole: "CEO",
    companyName: "Innovate",
    clientAvatarUrl: "",
    content: "We replaced three different tools with Moment of Truth. The dashboard is incredibly intuitive.",
    rating: 5,
    date: "2023-10-22",
    status: 'verified',
    isPublished: true
  },
];

const TestimonialContext = createContext();

export const TestimonialProvider = ({ children }) => {
  const [testimonials, setTestimonials] = useState(mockTestimonials);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchTestimonials = async () => {
    if (!user?.id) {
      setTestimonials(mockTestimonials);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        // Map snake_case DB columns to camelCase for the frontend
        const mapped = data.map(r => ({
          id: r.id,
          source: r.source || 'Custom',
          authorName: r.client_name || 'Anonymous',
          authorRole: r.job_role || '',
          companyName: r.company_name || '',
          companyLogo: r.company_logo_url || '',
          clientAvatarUrl: r.client_avatar_url || '',
          companyWebsite: r.company_website || '',
          linkedinUrl: r.linkedin_url || '',
          xUrl: r.x_url || '',
          reviewUrl: r.review_proof_url || '',
          youtubeLink: r.youtube_link || '',
          content: r.content || '',
          rating: r.rating || 5,
          date: r.created_at,
          // Same "effective date" the public RPCs use to pick the newest-25
          // window for free-tier pages — needed to mark overflow reviews.
          effectiveDate: r.source_published_at || r.created_at,
          status: r.status || 'under review',
          isPublished: r.is_published !== false
        }));
        setTestimonials(mapped);
      } else {
        setTestimonials([]);
      }
    } catch (err) {
      console.error('Error fetching testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, [user?.id]); // Depend on user ID to prevent re-fetching on token refresh

  return (
    <TestimonialContext.Provider value={{ testimonials, setTestimonials, fetchTestimonials, loading }}>
      {children}
    </TestimonialContext.Provider>
  );
};

export const useTestimonials = () => useContext(TestimonialContext);